import {
  Accordion,
  Button,
  Flex,
  SimpleGrid,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import {
  NumberInputControlled,
  Radio,
  RadioGroup,
  SelectControlled,
} from "../../../../components";
import { InfoCircle, Setting2 } from "iconsax-react";
import { useConfigContext } from "./ConfigProvider";
import { useTranslation } from "react-i18next";
import TitleDescription from "./TitleDescription";
import { Link } from "react-router-dom";
import { api } from "../../../../api";
import { client_selected } from "../../../../client";
import { formatCurrency } from "../../../../utils/currency";
import ModalOkxConnect from "./ModalOkx/ModalOkxConnect";

export default function AccordionConfig() {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const { form, product, isRefetching, states, modals, handlers } =
    useConfigContext();
  const brokerSelected = states.brokerSelected;
  const hasMoreThanOneOrganization = states.hasMoreThanOneOrganization;
  const isGenial = client_selected.name === "Genial";
  const isCrypto = product?.product?.market === "CRIPTO";
  const urlRegistration = product?.product?.url_registration;
  const linkReferral = !!urlRegistration
    ? urlRegistration
    : process.env.REACT_APP_BITGET_REFERRAL_LINK ||
      "https://www.bitget.com/pt/expressly?channelCode=bbvf&vipCode=hwe7&languageType=12";
  const disableNumber = states.disableNumber;
  const brokersAccountAvailable = states.brokersAccountAvailable;
  const valueSelect = states.valueSelect;
  const qtyContract = product?.plan?.qty_contract;
  const qtyContractIsOne = qtyContract === 1;
  const isSpider = client_selected.name !== "Empiricus";
  const hasBroker = form.watch("broker_integration_id");
  const urlPage = window.location.href;

  const shouldUseOkxCredentialsModal = states.shouldUseOkxCredentialsModal;

  const handleConnectBroker = async () => {
    const nameBroker = brokerSelected?.name;

    if (!nameBroker) return;

    if (nameBroker === "Bitget") {
      const res = await api.get<{ url: string }>("connect/bitget");
      const url = res.data.url;
      return window.open(url, "_blank");
    } else if (nameBroker === "OKX") {
      // If product is in the OKX credentials list, open modal instead of OAuth
      if (shouldUseOkxCredentialsModal) {
        return modals.setOpenModalOkxCredentials(true);
      }

      const res = await api.get<{ url: string }>("okx/connect", {
        params: { redirect_url: urlPage },
      });

      const url = res.data.url;

      return window.open(url, "_blank");
    } else {
      return;
    }
  };

  const handleOkxModalSuccess = () => {
    modals.setOpenModalOkxCredentials(false);
  };
  return (
    <>
      <Accordion.Item value="Config">
        <Accordion.Control p="0">
          <Flex
            id="lineOneCardProduct"
            justify="start"
            align={"center"}
            gap="8px"
          >
            <Setting2 size="24" color={theme.colors.basic[5]} />
            <Text
              fz={{ base: 18 }}
              lh={{ base: "155%" }}
              c={theme.colors.basic[5]}
            >
              {t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.title"
              )}
            </Text>
          </Flex>
        </Accordion.Control>
        <Accordion.Panel
          sx={{
            ".mantine-Accordion-content": {
              padding: "0px !important",
              margin: "20px 0",
            },
          }}
        >
          <Flex
            gap={{ base: 30 }}
            w="100%"
            direction={"column"}
            justify={"space-between"}
            style={{
              border: "1px solid",
              borderColor: theme.colors.basic[2],
              borderRadius: "20px",
            }}
            p={{ base: "10px", 991: "20px" }}
          >
            {isGenial ? null : (
              <>
                {hasMoreThanOneOrganization ? (
                  <Flex direction={"column"} gap="20px">
                    <TitleDescription
                      title={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder_2"
                      )}
                      description={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.text"
                      )}
                      hasDivider={"370px"}
                    />
                    <SimpleGrid
                      spacing="24px"
                      w="100%"
                      breakpoints={[
                        { minWidth: 700, cols: 2 },
                        { minWidth: 1200, cols: 3 },
                      ]}
                    >
                      <SelectControlled
                        name="brokerSelected"
                        label={t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.label_2"
                        )}
                        placeholder={t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder_2"
                        )}
                        maw="500px"
                        w={"100%"}
                        data={states.brokersAvailable}
                        disabled={!states.isEnabledToEdit}
                      />
                    </SimpleGrid>
                  </Flex>
                ) : null}
                {brokerSelected ? (
                  <Flex direction={"column"} gap="20px">
                    <TitleDescription
                      title={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.subtitle_1"
                      )}
                      description={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.description_1",
                        { broker: brokerSelected?.name || "" }
                      )}
                      hasDivider={"370px"}
                    />
                    <SimpleGrid
                      spacing="24px"
                      w="100%"
                      breakpoints={[
                        { minWidth: 700, cols: 2 },
                        { minWidth: 1200, cols: 3 },
                      ]}
                    >
                      <Tooltip
                        multiline
                        maw={"340px"}
                        label={t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1",
                          { broker: brokerSelected?.name || "" }
                        )}
                        withArrow
                      >
                        <Button
                          variant="primary"
                          onClick={handleConnectBroker}
                          w="100%"
                          maw="500px"
                        >
                          <Flex gap="4px">
                            {t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.button_1",
                              { broker: brokerSelected?.name || "" }
                            )}
                            <InfoCircle size={24} />
                          </Flex>
                        </Button>
                      </Tooltip>
                      <Tooltip
                        multiline
                        maw={"340px"}
                        label={t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_2",
                          { broker: brokerSelected?.name || "" }
                        )}
                        withArrow
                      >
                        <Button
                          variant="primaryOutline"
                          // onClick={handleConnectBroker}
                          component={Link}
                          w="100%"
                          maw="500px"
                          to={linkReferral}
                          target="_blank"
                        >
                          <Flex gap="4px">
                            {t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.button_2",
                              { broker: brokerSelected?.name || "" }
                            )}
                            <InfoCircle size={24} />
                          </Flex>
                        </Button>
                      </Tooltip>
                    </SimpleGrid>
                  </Flex>
                ) : null}
              </>
            )}

            <Flex direction={"column"} gap="20px">
              <TitleDescription
                title={t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.subtitle_2",
                  { name: product?.product?.title }
                )}
                description={t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.description_2"
                )}
                hasDivider={"370px"}
              />

              <Flex gap={{ base: "24px" }} w="100%" direction={"column"}>
                <SimpleGrid
                  w={"100%"}
                  spacing="24px"
                  breakpoints={[
                    { minWidth: 700, cols: 2 },
                    { minWidth: 1200, cols: 3 },
                    { minWidth: 1400, cols: 4 },
                  ]}
                >
                  {isCrypto ? (
                    <SelectControlled
                      name="broker_integration_id"
                      label={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.label"
                      )}
                      placeholder={
                        isRefetching
                          ? t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                            )
                          : t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder"
                            )
                      }
                      data={
                        isRefetching
                          ? [
                              {
                                value: "",
                                label: t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                                ),
                              },
                            ]
                          : brokersAccountAvailable
                      }
                      w={"100%"}
                      value={
                        isRefetching
                          ? t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                            )
                          : !!form.watch("broker_integration_id")
                          ? form.watch("broker_integration_id")?.toString()
                          : undefined
                      }
                      disabled={!states.isEnabledToEdit}
                    />
                  ) : (
                    <SelectControlled
                      name="broker_integration_id"
                      label={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.label_2"
                      )}
                      placeholder={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder_2"
                      )}
                      w={"100%"}
                      data={brokersAccountAvailable}
                      disabled={!states.isEnabledToEdit}
                    />
                  )}

                  <NumberInputControlled
                    name="value"
                    min={valueSelect.min_value_config}
                    max={valueSelect.max_value_config}
                    step={valueSelect.multiply_value_config}
                    w={"100%"}
                    disabled={
                      !valueSelect.has_value_config || !states.isEnabledToEdit
                    }
                    placeholder={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.placeholder"
                    )}
                    parser={(value) => value.replace(/\s?US\$|,*/g, "")}
                    formatter={(value) => {
                      const numericValue = parseFloat(value);
                      if (!Number.isNaN(numericValue)) {
                        const roundedValue =
                          Math.round(
                            numericValue / valueSelect.multiply_value_config
                          ) * valueSelect.multiply_value_config;

                        return `US$ ${roundedValue}`.replace(
                          /\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }

                      return "US$ ";
                    }}
                    label={
                      <Flex
                        gap={8}
                        align="center"
                        justify={"space-between"}
                        w="100%"
                      >
                        {t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.label"
                        )}
                        <Tooltip
                          multiline
                          label={
                            <Flex maw="340px" w={"340px"}>
                              <Text
                                style={{
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                  width: "340px",
                                }}
                              >
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_1",
                                  {
                                    min: formatCurrency(
                                      valueSelect.min_value_config,
                                      false
                                    ),
                                  }
                                )}
                                <br />
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_2",
                                  {
                                    max: formatCurrency(
                                      valueSelect.max_value_config,
                                      false
                                    ),
                                  }
                                )}
                                <br />
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_3",
                                  {
                                    multiply: formatCurrency(
                                      valueSelect.multiply_value_config,
                                      false
                                    ),
                                  }
                                )}
                              </Text>
                            </Flex>
                          }
                          maw="360px"
                          withArrow
                          transitionProps={{ duration: 200 }}
                        >
                          <InfoCircle size={14} color={theme.colors.basic[5]} />
                        </Tooltip>
                      </Flex>
                    }
                  />
                  {qtyContractIsOne ? null : (
                    <NumberInputControlled
                      name="qty"
                      min={1}
                      max={disableNumber.max}
                      w={"100%"}
                      disabled={disableNumber.disable}
                      placeholder={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_qty.placeholder"
                      )}
                      label={
                        <Flex
                          gap={8}
                          align="center"
                          justify={"space-between"}
                          w="100%"
                        >
                          {disableNumber.label}
                          {disableNumber.tooltip ? (
                            <Tooltip
                              multiline
                              label={disableNumber.tooltip}
                              withArrow
                              w={"360px"}
                              transitionProps={{ duration: 200 }}
                            >
                              <InfoCircle
                                size={14}
                                color={theme.colors.basic[5]}
                              />
                            </Tooltip>
                          ) : null}
                        </Flex>
                      }
                    />
                  )}
                  {!!product?.product?.has_reverse_order &&
                  isSpider &&
                  !isCrypto ? (
                    <RadioGroup
                      label={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.label"
                      )}
                      value={
                        !!form.watch("is_inverse_side") ? "inversa" : "favor"
                      }
                      onChange={(value) =>
                        form.setValue("is_inverse_side", value === "inversa")
                      }
                      w={"100%"}
                    >
                      <Flex mt="8px" gap="24px" h="56px" align={"center"}>
                        <Radio
                          value="favor"
                          label={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.inverse"
                          )}
                          disabled={!states.isEnabledToEdit}
                        />
                        <Radio
                          value="inversa"
                          label={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.favor"
                          )}
                          disabled={!states.isEnabledToEdit}
                        />
                      </Flex>
                    </RadioGroup>
                  ) : null}
                </SimpleGrid>
                <SimpleGrid
                  spacing="24px"
                  w="100%"
                  breakpoints={[
                    { minWidth: 700, cols: 2 },
                    { minWidth: 1200, cols: 3 },
                    { minWidth: 1400, cols: 2 },
                  ]}
                >
                  <Button
                    type="button"
                    variant={"primary"}
                    w={"100%"}
                    styles={{
                      label: {
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        textAlign: "center",
                      },
                    }}
                    disabled={
                      states.disabledEdit
                        ? false
                        : !states.isDirty || !hasBroker
                    }
                    onClick={
                      states.disabledEdit
                        ? () => states.setDisabledEdit(false)
                        : !!product?.product
                            ?.has_configuration_changes_quarantine
                        ? () => {
                            modals.setOpenModalQuarantine(true);
                            states.setDisabledEdit(true);
                          }
                        : handlers.handleUpdateAlgorithm
                    }
                  >
                    {states.disabledEdit
                      ? t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.button_modify_config"
                        )
                      : states.isAlgorithmRunning
                      ? t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.button_submit_config"
                        )
                      : t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.button_activate_config"
                        )}
                  </Button>
                </SimpleGrid>
              </Flex>
              {states.isPendingScheduledActions ? (
                <Flex
                  gap="10px"
                  bg="complementaryOpacity.4"
                  p="10.5px 12px"
                  w="100%"
                  align="center"
                  justify="center"
                  sx={{ borderRadius: "8px" }}
                >
                  <Flex w="24px">
                    <Tooltip
                      multiline
                      w={236}
                      label={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.tooltip"
                      )}
                      withArrow
                      transitionProps={{ duration: 200 }}
                    >
                      <InfoCircle
                        size={24}
                        color={theme.colors.complementary[4]}
                      />
                    </Tooltip>
                  </Flex>
                  <Text size={14} lh="19px" color="complementary.4" fw={500}>
                    {t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.text_info"
                    )}
                  </Text>
                </Flex>
              ) : !states.isAlgorithmRunning ? (
                <Flex
                  gap="10px"
                  bg="alertsOpacity.0"
                  p="10.5px 12px"
                  w="100%"
                  align="center"
                  justify="center"
                  sx={{ borderRadius: "8px" }}
                >
                  <Flex w="24px">
                    <InfoCircle
                      size={24}
                      color={theme.colors.complementary[4]}
                    />
                  </Flex>
                  <Text size={14} lh="19px" color="alerts.0" fw={500}>
                    {t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.text_info_inactive"
                    )}
                  </Text>
                </Flex>
              ) : null}
            </Flex>
          </Flex>
        </Accordion.Panel>
      </Accordion.Item>

      <ModalOkxConnect
        open={modals.openModalOkxCredentials}
        setClose={() => modals.setOpenModalOkxCredentials(false)}
        setSuccess={handleOkxModalSuccess}
      />
    </>
  );
}
